import React from "react";
import Image from "next/image";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import type { EventType } from "@/global/store/data/types";
import { TSlide } from "./types";
import { IoMedia3dView } from "./IoMedia3dView";
import { GroupTicketSeatMap } from "@/shared/modules/InteractiveSeatMap/GroupTicketSeatMap";

export function createSlides(
  viewId3dSelected: string,
  SeatMap3d: React.FC,
  teamConfig: TeamConfig,
  event?: EventType | null,
): TSlide[] {
  const slides: TSlide[] = []; // slides array for the carousel
  const suiteImages = teamConfig?.seatMapConfigs?.suiteImages; // suite images from team config
  const specificEventCodeSectionImages = teamConfig?.seatMapConfigs?.specificEventCodeSectionImages; // prettier-ignore
  const specificSectionImages = teamConfig?.seatMapConfigs?.specificSectionImages; // prettier-ignore
  const specificEventCodeImages = teamConfig?.seatMapConfigs?.specificEventCodeImages; // prettier-ignore
  const defaultSlide: TSlide = { type: "3d", component: <SeatMap3d /> }; // adding 3d view as default slide

  const isHiddenByCategory =
    teamConfig?.seatMapConfigs?.hide3dViewFromSliderByCategory?.some(
      (category) => category.toLowerCase() === event?.categories?.toLowerCase(),
    );

  if (teamConfig?.seatMapConfigs?.hide3dViewFromSlider) {
    if (
      typeof teamConfig?.seatMapConfigs?.hide3dViewFromSlider === "boolean" &&
      !teamConfig?.seatMapConfigs?.hide3dViewFromSlider &&
      !isHiddenByCategory
    ) {
      slides.push(defaultSlide);
    }

    if (
      typeof teamConfig?.seatMapConfigs?.hide3dViewFromSlider === "object" &&
      !teamConfig?.seatMapConfigs?.hide3dViewFromSlider?.includes(
        viewId3dSelected.replace("S_", ""),
      ) &&
      !isHiddenByCategory
    ) {
      slides.push(defaultSlide);
    }
  } else if (!isHiddenByCategory) {
    slides.push(defaultSlide);
  }

  if (teamConfig?.seatMapConfigs?.isGroupTicketSeatMapEnabled) {
    slides.push({
      type: "2d",
      component: <GroupTicketSeatMap />,
    });
  }

  if (!suiteImages) {
    return slides as TSlide[];
  } // if no suite images, return default slide

  let imagesForSlides: string[] = [];

  // if the config suiteImages is a plain array with image urls
  // then show the same images for all 3d views
  if (Array.isArray(suiteImages) && suiteImages?.length > 0) {
    imagesForSlides = suiteImages;
  }

  if (!!specificSectionImages) {
    // specificSectionImages is Record<Record<string[]>>
    const selected3dSection = viewId3dSelected.replace("S_", "");
    const sectionImages = specificSectionImages[selected3dSection];
    if (sectionImages) imagesForSlides = sectionImages;
  }

  if (!!specificEventCodeSectionImages && event?.eventCode) {
    // specificEventCodeSectionImages is Record<Record<string[]>>
    const eventImages = specificEventCodeSectionImages[event.eventCode];
    if (Array.isArray(eventImages)) {
      if (eventImages) imagesForSlides = [...imagesForSlides, ...eventImages];
    }
  }

  // if config suiteImages is an object with section name image mapping
  // then constructing the slider only for the selected section
  if (typeof suiteImages === "object" && !Array.isArray(suiteImages)) {
    const selected3dSection = viewId3dSelected.replace("S_", "");
    imagesForSlides = suiteImages[selected3dSection] || [];
  }

  if (!!specificEventCodeImages && event?.eventCode) {
    const eventImages = specificEventCodeImages[event.eventCode];
    if (eventImages) imagesForSlides = [...imagesForSlides, ...eventImages];
  }

  imagesForSlides.forEach((url: string) => {
    // detect if the url is a io-media, 3ddigitalvenue.com, suiteview.com, or 3dspaces.american3dvirtualtours.com 3D view
    if (
      url.includes("io-media") ||
      url.includes("3ddigitalvenue.com") ||
      url.includes("suiteview.com") ||
      url.includes("3dspaces.american3dvirtualtours.com")
    ) {
      return slides.push({
        type: "3d",
        component: <IoMedia3dView url={url} />,
      });
    }

    // if it's regular image url
    return slides.push({
      type: "image",
      component: (
        <Image
          src={url}
          alt="suite"
          width={2568}
          height={1445}
          style={{
            width: "100%",
            height: "auto",
            maxHeight: 410,
            objectFit: "contain",
          }}
        />
      ),
    });
  });

  return slides as TSlide[];
}
